import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { acknowledgeAlarm } from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import searchIconSrc from "../../../../assets/search.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import {
  getAlarmSeverity,
  getAlarmDisplayPath,
  matchesTextFilter,
  sortAlarms,
} from "./alarm-display-utils";

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";

// ── Constants ────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "all", label: "All States" },
  { value: "active_unacked", label: "Active, Unacked" },
  { value: "active_acked", label: "Active, Acked" },
  { value: "clear_unacked", label: "Clear, Unacked" },
  { value: "clear_acked", label: "Clear, Acked" },
];

const SEVERITY_OPTIONS = [
  { value: "all", label: "All Severities" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "priority", label: "Priority" },
  { value: "source", label: "Source" },
  { value: "message", label: "Message" },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const MODE_LABELS = {
  above: "Above",
  below: "Below",
  between: "Between",
  outside_range: "Outside Range",
};

const SEVERITY_BADGE_COLORS = {
  critical: { bg: "var(--gw-danger-bg)", color: "var(--gw-danger)", border: "var(--gw-danger-border)" },
  high: { bg: "rgba(249,115,22,0.12)", color: "#e68900", border: "rgba(249,115,22,0.25)" },
  medium: { bg: "rgba(200,150,0,0.12)", color: "#b08800", border: "rgba(200,150,0,0.25)" },
  low: { bg: "var(--gw-accent-active)", color: "var(--gw-accent)", border: "rgba(80,140,255,0.25)" },
};

// ── Helpers ──────────────────────────────────────────────────────────

function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60000) return "Just now";
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function stateLabel(alarm) {
  const active = alarm?.state !== "clear";
  const acked = !!alarm?.acknowledged;
  if (active && !acked) return "Active, Unacked";
  if (active && acked) return "Active, Acked";
  if (!active && !acked) return "Clear, Unacked";
  return "Clear, Acked";
}

function matchesStatusFilter(alarm, filter) {
  if (!filter || filter === "all") return true;
  const active = alarm?.state !== "clear";
  const acked = !!alarm?.acknowledged;
  switch (filter) {
    case "active_unacked": return active && !acked;
    case "active_acked": return active && acked;
    case "clear_unacked": return !active && !acked;
    case "clear_acked": return !active && acked;
    default: return true;
  }
}

function getRowClassName(alarm) {
  const active = alarm?.state !== "clear";
  const acked = !!alarm?.acknowledged;
  const severity = getAlarmSeverity(alarm);
  let stateClass;
  if (active && !acked) stateClass = "alarmRow--activeUnacked";
  else if (active && acked) stateClass = "alarmRow--activeAcked";
  else if (!active && !acked) stateClass = "alarmRow--clearUnacked";
  else stateClass = "alarmRow--clearAcked";
  return `alarmRow ${stateClass} alarmRow--${severity}`;
}

// ── Small Components ─────────────────────────────────────────────────

const SEVERITY_DOT_COLORS = {
  critical: "var(--gw-danger)",
  high: "#f97316",
  medium: "#eab308",
  low: "var(--gw-accent)",
};

const SeverityDot = ({ severity }) => (
  <div style={{
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
    background: SEVERITY_DOT_COLORS[severity] || SEVERITY_DOT_COLORS.low,
  }} title={severity} />
);

const SeverityBadge = ({ severity }) => {
  const c = SEVERITY_BADGE_COLORS[severity] || SEVERITY_BADGE_COLORS.medium;
  return (
    <span style={{
      fontSize: 10, textTransform: "capitalize", padding: "2px 7px", borderRadius: 5,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontWeight: 600, whiteSpace: "nowrap",
    }}>{severity}</span>
  );
};

const ConnectionDot = ({ connected }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--gw-muted)" }}>
    <div style={{
      width: 6, height: 6, borderRadius: "50%",
      background: connected ? "#4caf50" : "#ef4444",
    }} />
    {connected ? "Live" : "Disconnected"}
  </div>
);

// ── WebSocket Hook ───────────────────────────────────────────────────

function useAlarmStream() {
  const [alarms, setAlarms] = useState({});
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    let disposed = false;

    function connect() {
      if (disposed) return;
      const base = CORE_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        if (!disposed) setConnected(true);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "pong" || msg.type === "ping") return;
          if (msg.type === "alarm_initial") {
            const map = {};
            (msg.alarms || []).forEach((a) => { map[a.alarm_id] = a; });
            setAlarms(map);
          } else if (msg.type === "alarm_update") {
            setAlarms((prev) => {
              const next = { ...prev };
              (msg.alarms || []).forEach((a) => { next[a.alarm_id] = a; });
              return next;
            });
          }
        } catch { /* ignore non-JSON */ }
      };

      ws.onclose = () => {
        if (disposed) return;
        setConnected(false);
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectTimer.current = setTimeout(() => {
          reconnectAttempts.current += 1;
          connect();
        }, delay);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);

    return () => {
      disposed = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return { alarms, connected };
}

// ── Main Page ────────────────────────────────────────────────────────

const AlarmManagementPage = () => {
  const canConfigure = usePermission("configure");
  const { alarms: alarmMap, connected } = useAlarmStream();
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [acknowledging, setAcknowledging] = useState(false);
  const [confirmAckAll, setConfirmAckAll] = useState(false);
  const notify = useNotify();

  // ── Filtering & sorting ────────────────────────────────────────

  const filtered = useMemo(() => {
    const all = Object.values(alarmMap);
    const result = all.filter((a) => {
      if (!matchesStatusFilter(a, statusFilter)) return false;
      if (severityFilter !== "all" && getAlarmSeverity(a) !== severityFilter) return false;
      if (!matchesTextFilter(a, searchText)) return false;
      return true;
    });
    return sortAlarms(result, sortBy);
  }, [alarmMap, statusFilter, severityFilter, searchText, sortBy]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [statusFilter, severityFilter, searchText, sortBy]);

  // ── Pagination ─────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageAlarms = filtered.slice(page * pageSize, (page + 1) * pageSize);

  // ── Stats ──────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const all = Object.values(alarmMap);
    let active = 0, unacked = 0;
    all.forEach((a) => {
      if (a.state !== "clear") active++;
      if (!a.acknowledged) unacked++;
    });
    return { total: all.length, active, unacked };
  }, [alarmMap]);

  // ── Selection ──────────────────────────────────────────────────

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const unackedOnPage = pageAlarms.filter((a) => !a.acknowledged).map((a) => a.alarm_id);
    setSelectedIds((prev) => {
      const allSelected = unackedOnPage.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        unackedOnPage.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...unackedOnPage]);
    });
  }, [pageAlarms]);

  // ── Acknowledge ────────────────────────────────────────────────

  const handleAck = useCallback(async (ids) => {
    if (!ids.length) return;
    setAcknowledging(true);
    try {
      await Promise.all(ids.map((id) => acknowledgeAlarm(id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      const count = ids.length;
      notify({ type: "success", title: "Alarms Acknowledged", message: `${count} alarm${count !== 1 ? "s" : ""} acknowledged successfully.` });
    } catch (err) {
      notify({ type: "error", title: "Acknowledge Failed", message: err.message });
    } finally {
      setAcknowledging(false);
    }
  }, [notify]);

  const handleAckSelected = useCallback(() => {
    handleAck([...selectedIds]);
  }, [selectedIds, handleAck]);

  const handleAckAllVisible = useCallback(() => {
    const ids = filtered.filter((a) => !a.acknowledged).map((a) => a.alarm_id);
    setConfirmAckAll(false);
    handleAck(ids);
  }, [filtered, handleAck]);

  const visibleUnacked = filtered.filter((a) => !a.acknowledged).length;

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">

        {/* Header */}
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Alarm Management</div>
            <div className="gwPageSubtitle" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>
                {stats.total} alarm{stats.total !== 1 ? "s" : ""}
                {" \u00b7 "}{stats.active} active
                {" \u00b7 "}{stats.unacked} unacknowledged
              </span>
              <ConnectionDot connected={connected} />
            </div>
          </div>
          <div className="alarmHeaderActions">
            <button className="gwPrimaryBtn" type="button"
              disabled={acknowledging || selectedIds.size === 0 || !canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              onClick={handleAckSelected}>
              {acknowledging ? "Acknowledging..." : `Ack Selected (${selectedIds.size})`}
            </button>
            <button className="gwGhostBtn" type="button"
              disabled={acknowledging || visibleUnacked === 0 || !canConfigure}
              title={canConfigure ? undefined : "Requires configure permission"}
              onClick={() => setConfirmAckAll(true)}>
              Ack All Visible ({visibleUnacked})
            </button>
          </div>
        </div>

        {/* Confirm Ack All Modal */}
        {confirmAckAll && (
          <div className="gwOverlay" onClick={() => setConfirmAckAll(false)}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">Acknowledge All Visible Alarms</div>
                  <div className="gwModalSubtitle">This will acknowledge {visibleUnacked} unacknowledged alarm{visibleUnacked !== 1 ? "s" : ""}</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setConfirmAckAll(false)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                  Are you sure you want to acknowledge all {visibleUnacked} visible unacknowledged alarm{visibleUnacked !== 1 ? "s" : ""}? This action cannot be undone.
                </p>
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "flex-end" }}>
                <button className="gwGhostBtn" type="button" onClick={() => setConfirmAckAll(false)}>Cancel</button>
                <button className="gwDangerBtn" type="button" onClick={handleAckAllVisible}
                  disabled={acknowledging || !canConfigure}
                  title={canConfigure ? undefined : "Requires configure permission"}>
                  {acknowledging ? "Acknowledging..." : "Acknowledge All"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="gwCardSection">
          <div className="gwCardSectionBody" style={{ padding: "12px 16px" }}>
            <div className="alarmFilters">
              <div className="alarmFilterItem">
                <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
              </div>
              <div className="alarmFilterItem">
                <Select value={severityFilter} onChange={setSeverityFilter} options={SEVERITY_OPTIONS} />
              </div>
              <div className="alarmFilterItem">
                <Select value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
              </div>
              <div className="gwSearch alarmSearchItem" style={{ height: 30 }}>
                <img src={searchIconSrc} alt="" className="gwSearchIcon" />
                <input placeholder="Search..." value={searchText}
                  onChange={(e) => setSearchText(e.target.value)} style={{ fontSize: 12 }} />
              </div>
            </div>
          </div>
        </div>

        {/* Alarm List */}
        <div className="gwCardSection">
          <div className="gwSectionTitle">
            <span>Alarms ({filtered.length})</span>
          </div>
          <div className="gwCardSectionBody" style={{ padding: 0, gap: 0 }}>

            {/* Column Header */}
            <div className="alarmColHeader">
              <div className="alarmCell--check">
                <input type="checkbox" style={{ cursor: "pointer" }}
                  checked={pageAlarms.filter((a) => !a.acknowledged).length > 0 &&
                    pageAlarms.filter((a) => !a.acknowledged).every((a) => selectedIds.has(a.alarm_id))}
                  onChange={toggleSelectAll} />
              </div>
              <div className="alarmCell--dot" />
              <div className="alarmCell--state">State</div>
              <div className="alarmCell--source">Source</div>
              <div className="alarmCell--message">Message</div>
              <div className="alarmCell--mode">Mode</div>
              <div className="alarmCell--time">Time</div>
              <div className="alarmCell--ack" />
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                {Object.keys(alarmMap).length === 0
                  ? "No active alarms. Alarms will appear here when triggered."
                  : "No alarms match the current filters."}
              </div>
            )}

            {/* Rows */}
            {pageAlarms.map((alarm) => {
              const severity = getAlarmSeverity(alarm);
              const isUnacked = !alarm.acknowledged;
              const isSelected = selectedIds.has(alarm.alarm_id);

              return (
                <div key={alarm.alarm_id} className={getRowClassName(alarm)}
                  data-gw-resource="live_alarms" data-gw-id={alarm.alarm_id} data-gw-name={alarm.name || alarm.message}>
                  <div className="alarmCell--check">
                    {isUnacked && (
                      <input type="checkbox" checked={isSelected}
                        onChange={() => toggleSelect(alarm.alarm_id)}
                        style={{ cursor: "pointer" }} />
                    )}
                  </div>
                  <div className="alarmCell--dot">
                    <SeverityDot severity={severity} />
                  </div>
                  <div className="alarmCell--state">
                    <div style={{ fontSize: 12 }}>{stateLabel(alarm)}</div>
                    <SeverityBadge severity={severity} />
                  </div>
                  <div className="alarmCell--source">
                    {alarm.name && <div style={{ fontWeight: 500 }}>{alarm.name}</div>}
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{getAlarmDisplayPath(alarm)}</div>
                  </div>
                  <div className="alarmCell--message">
                    {alarm.message || "\u2014"}
                  </div>
                  <div className="alarmCell--mode">
                    <div>{MODE_LABELS[alarm.mode] || alarm.mode}</div>
                    <div style={{ fontSize: 10, opacity: 0.7 }}>
                      {alarm.setpoint}{alarm.setpoint_high != null ? `\u2013${alarm.setpoint_high}` : ""}
                    </div>
                  </div>
                  <div className="alarmCell--time">
                    {formatTime(alarm.active_at)}
                  </div>
                  <div className="alarmCell--ack">
                    {isUnacked && (
                      <button className="alarmAckBtn" type="button"
                        disabled={acknowledging || !canConfigure}
                        title={canConfigure ? undefined : "Requires configure permission"}
                        onClick={() => handleAck([alarm.alarm_id])}>
                        Ack
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {filtered.length > 0 && (
              <div className="alarmPagination">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>Rows per page:</span>
                  <Select value={pageSize} onChange={(v) => { setPageSize(v); setPage(0); }}
                    options={PAGE_SIZE_OPTIONS} />
                </div>
                <span>
                  {page * pageSize + 1}{"\u2013"}{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
                </span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button className="gwGhostBtn" type="button" disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                    style={{ fontSize: 11, height: 26, padding: "0 10px" }}>
                    Prev
                  </button>
                  <button className="gwGhostBtn" type="button" disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    style={{ fontSize: 11, height: 26, padding: "0 10px" }}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export { AlarmManagementPage };
